import React, { useEffect, useState } from "react";
import { FloatingLabel, Form, Button, Card, Toast } from "react-bootstrap";
import Header from "../../components/Header";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import styles from "./Usuario.module.css";
import { useLoading } from "../../components/LoadingContext";

import {
  Organica,
  Establecimiento,
  ReparticionGde,
  SectorGde,
  FormData,
} from "../../types/interfaces";

function Usuario() {
  const { setLoading } = useLoading();
  const [user, setUser] = useState<any>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [originalFormData, setOriginalFormData] = useState<any>({});
  const [editModeDataLoaded, setEditModeDataLoaded] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [requestedChanges, setRequestedChanges] = useState<any>({});
  const [organicas, setOrganicas] = useState<Organica[]>([]);
  const [establecimientos, setEstablecimientos] = useState<Establecimiento[]>(
    []
  );
  const [reparticionesGde, setReparticionesGde] = useState<ReparticionGde[]>(
    []
  );
  const [sectoresGde, setSectoresGde] = useState<SectorGde[]>([]);
  const [filteredSectores, setFilteredSectores] = useState<SectorGde[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [
          userResponse,
        ] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
            method: "GET",
            credentials: "include",
          }),
        ]);

        const userData = await userResponse.json();

        setUser(userData);

      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoading]);

  useEffect(() => {
    if (formData.reparticion_gde && formData.reparticion_gde.id) {
      const sectorsForReparticion = sectoresGde.filter(
        (sector) => sector.reparticion_id === formData.reparticion_gde.id
      );
      setFilteredSectores(sectorsForReparticion);
    } else {
      setFilteredSectores(sectoresGde);
    }
  }, [sectoresGde, formData.reparticion_gde]);

  const loadEditModeData = async () => {
    if (editModeDataLoaded) return;

    setLoading(true);
    try {
      const [
        requestDataResponse,
        organicasResponse,
        establecimientosResponse,
        reparticionesResponse,
        sectoresResponse,
      ] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/user/changeRequest/:id`, {
          method: "GET",
          credentials: "include",
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/allOrganicas`, {
          method: "GET",
          credentials: "include",
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/allEstablecimientos`, {
          method: "GET",
          credentials: "include",
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/allReparticionesGde`, {
          method: "GET",
          credentials: "include",
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/allSectoresGde`, {
          method: "GET",
          credentials: "include",
        }),
      ]);

      const requestData = await requestDataResponse.json();
      const organicasData = await organicasResponse.json();
      const establecimientosData = await establecimientosResponse.json();
      const reparticionesData = await reparticionesResponse.json();
      const sectoresData = await sectoresResponse.json();

      setOrganicas(organicasData);
      setEstablecimientos(establecimientosData);
      setReparticionesGde(reparticionesData);
      setSectoresGde(sectoresData);
      setFilteredSectores(sectoresData);
      setEditModeDataLoaded(true);

      if (requestData.length > 0) {
        const cambiosSolicitados = JSON.parse(
          requestData[0].cambios_solicitados
        );
        setRequestedChanges(cambiosSolicitados);
        setFormData(cambiosSolicitados);
      } else {
        setFormData(user.funcionario || {});
        setRequestedChanges({});
        setOriginalFormData(user.funcionario || {});
      }      
    } catch (error) {
      console.error("Error cargando datos auxiliares:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReparticionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    const selectedReparticion = reparticionesGde.find(
      (r) => r.nombre === value
    );

    setFormData((prevData: any) => ({
      ...prevData,
      reparticion_gde: { nombre: value, id: selectedReparticion?.id },
    }));

    const sectorsForReparticion = sectoresGde.filter(
      (sector) => sector.reparticion_id === selectedReparticion?.id
    );
    setFilteredSectores(sectorsForReparticion);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    const findIdByName = (name: string, list: any[]) => {
      const selectedItem = list.find((item) => item.nombre === name);
      return selectedItem ? selectedItem.id : null;
    };

    setFormData((prevData: any) => {
      switch (name) {
        case "organica.nombre":
          const organicaId = findIdByName(value, organicas);
          return { ...prevData, organica: { nombre: value, id: organicaId } };
        case "establecimiento.nombre":
          const establecimientoId = findIdByName(value, establecimientos);
          return {
            ...prevData,
            establecimiento: { nombre: value, id: establecimientoId },
          };
        case "reparticion_gde.nombre":
          const reparticionId = findIdByName(value, reparticionesGde);
          return {
            ...prevData,
            reparticion_gde: { nombre: value, id: reparticionId },
          };
        case "sector_gde.nombre":
          const sectorId = findIdByName(value, sectoresGde);
          return { ...prevData, sector_gde: { nombre: value, id: sectorId } };
        default:
          return prevData;
      }
    });
  };

  const normalizeAssociations = (assoc: any) => {
    if (assoc && typeof assoc === "object") {
      return {
        id: assoc.id,
        nombre: assoc.nombre,
      };
    }
    return assoc;
  };

  const normalizeData = (data: any) => {
    const {
      id,
      activo,
      reparticion_id,
      sector_id,
      organica_id,
      correo,
      revista,
      establecimiento_id,
      ...normalized
    } = data;

    normalized.establecimiento = normalizeAssociations(
      normalized.establecimiento
    );
    normalized.organica = normalizeAssociations(normalized.organica);
    normalized.reparticion_gde = normalizeAssociations(
      normalized.reparticion_gde
    );
    normalized.sector_gde = normalizeAssociations(normalized.sector_gde);

    return normalized;
  };

  const deepEqual = (obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return false;
    if (typeof obj1 !== "object" || typeof obj2 !== "object") return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key]))
        return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const newErrors: any = {};
    let hasErrors = false;

    if (!formData.funcionario) {
      newErrors.funcionario = "Este campo es obligatorio";
      hasErrors = true;
    } else if (formData.funcionario.length < 5) {
      newErrors.funcionario = "Este campo debe tener al menos 5 caracteres";
      hasErrors = true;
    }
    if (!formData.organica || !formData.organica.nombre) {
      newErrors.organica = "Este campo es obligatorio";
      hasErrors = true;
    }
    if (!formData.establecimiento || !formData.establecimiento.nombre) {
      newErrors.establecimiento = "Este campo es obligatorio";
      hasErrors = true;
    }
    if (!formData.ubi_adicional) {
      newErrors.ubi_adicional = "Este campo es obligatorio";
      hasErrors = true;
    }
    if (!formData.interno) {
      newErrors.interno = "Este campo es obligatorio";
      hasErrors = true;
    } else if (isNaN(Number(formData.interno))) {
      newErrors.interno = "Este campo debe ser un número";
      hasErrors = true;
    }
    if (!formData.usuario_gde) {
      newErrors.usuario_gde = "Este campo es obligatorio";
      hasErrors = true;
    } else if (formData.usuario_gde.length < 3) {
      newErrors.usuario_gde = "Este campo debe tener al menos 3 caracteres";
      hasErrors = true;
    }
    if (!formData.reparticion_gde || !formData.reparticion_gde.nombre) {
      newErrors.reparticion_gde = "Este campo es obligatorio";
      hasErrors = true;
    }
    if (!formData.sector_gde || !formData.sector_gde.nombre) {
      newErrors.sector_gde = "Este campo es obligatorio";
      hasErrors = true;
    }

    const normalizedFormData = normalizeData(formData);
    const normalizedUserData = normalizeData(user.funcionario);
    const normalizedRequestedChanges = normalizeData(requestedChanges);

    const isChanged = !deepEqual(normalizedFormData, normalizedUserData);

    if (!isChanged) {
      newErrors.solicitarCambios = "Estos datos ya son los asignados";
      hasErrors = true;
    }

    const isRequestAlreadySent = deepEqual(
      normalizedFormData,
      normalizedRequestedChanges
    );

    if (isRequestAlreadySent) {
      newErrors.solicitarCambios = "Estos cambios ya han sido solicitados";
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      setLoading(false);
      return;
    } else {
      setErrors({}); 
    }

    const filteredFormData: FormData = {};

    if (formData.funcionario) {
      filteredFormData.funcionario = formData.funcionario;
    }
    if (formData.interno) {
      filteredFormData.interno = formData.interno;
    }
    if (formData.ubi_adicional) {
      filteredFormData.ubi_adicional = formData.ubi_adicional;
    }
    if (formData.usuario_gde) {
      filteredFormData.usuario_gde = formData.usuario_gde;
    }

    if (
      formData.establecimiento &&
      formData.establecimiento.id &&
      formData.establecimiento.nombre
    ) {
      filteredFormData.establecimiento = {
        id: formData.establecimiento.id,
        nombre: formData.establecimiento.nombre,
      };
    }
    if (formData.organica && formData.organica.id && formData.organica.nombre) {
      filteredFormData.organica = {
        id: formData.organica.id,
        nombre: formData.organica.nombre,
      };
    }
    if (
      formData.reparticion_gde &&
      formData.reparticion_gde.id &&
      formData.reparticion_gde.nombre
    ) {
      filteredFormData.reparticion_gde = {
        id: formData.reparticion_gde.id,
        nombre: formData.reparticion_gde.nombre,
      };
    }
    if (
      formData.sector_gde &&
      formData.sector_gde.id &&
      formData.sector_gde.nombre
    ) {
      filteredFormData.sector_gde = {
        id: formData.sector_gde.id,
        nombre: formData.sector_gde.nombre,
      };
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/changeRequest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          funcionario_id: user.funcionario.id,
          cambios_solicitados: filteredFormData,
        }),
      });

      if (response.ok) {
        setToastMessage("Solicitud enviada con éxito");
        setShowToast(true);

        setRequestedChanges(filteredFormData);
        setEditMode(false);
      } else {
        const errorData = await response.json();
        console.error("Error al enviar la solicitud de cambio", errorData);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (Object.keys(requestedChanges).length === 0) {
      setFormData(originalFormData);
    } else {
      setFormData(requestedChanges);
    }
    setEditMode(false);
    setErrors("");
  };

  return (
    <>
      <Header />
      <NavBar />
      <div className={styles.profileContainer}>
        <h2 className={styles.titlesRoutes}>Mis datos</h2>
        {user && (
          <Card className={`mb-2 ${styles.card}`}>
            <section className={styles.userdataSection}>
              <div className={styles.userdataContainer}>
                {editMode ? (
                  <Form onSubmit={handleSubmit}>
                    <FloatingLabel
                      controlId="floatingInputFuncionario"
                      label="Nombre(s) y apellido(s)"
                      className="mb-3"
                    >
                      <Form.Control
                        type="text"
                        placeholder="Nombre(s) y apellido(s)"
                        name="funcionario"
                        value={formData.funcionario}
                        onChange={handleInputChange}
                      />
                    </FloatingLabel>

                    {errors.funcionario && (
                      <div className={styles.errors}>{errors.funcionario}</div>
                    )}

                    <FloatingLabel
                      controlId="floatingSelectOrganica"
                      label="Orgánica"
                      className="mb-3"
                    >
                      <Form.Select
                        aria-label="Orgánica"
                        name="organica.nombre"
                        value={formData.organica?.nombre || ""}
                        onChange={handleSelectChange}
                      >
                        <option value="" disabled>
                          Seleccionar orgánica
                        </option>
                        {organicas
                          .slice()
                          .sort((a, b) => a.nombre.localeCompare(b.nombre))
                          .map((organica) => (
                            <option key={organica.id} value={organica.nombre}>
                              {organica.nombre}
                            </option>
                          ))}
                      </Form.Select>
                    </FloatingLabel>

                    {errors.organica && (
                      <div className={styles.errors}>{errors.organica}</div>
                    )}

                    <FloatingLabel
                      controlId="floatingSelectEstablecimiento"
                      label="Establecimiento"
                      className="mb-3"
                    >
                      <Form.Select
                        aria-label="Establecimiento"
                        name="establecimiento.nombre"
                        value={formData.establecimiento?.nombre || ""}
                        onChange={handleSelectChange}
                      >
                        <option value="" disabled>
                          Seleccionar establecimiento
                        </option>
                        {establecimientos
                          .slice()
                          .sort((a, b) => a.nombre.localeCompare(b.nombre))
                          .map((establecimiento) => (
                            <option
                              key={establecimiento.id}
                              value={establecimiento.nombre}
                            >
                              {establecimiento.nombre}
                            </option>
                          ))}
                      </Form.Select>
                    </FloatingLabel>

                    {errors.establecimiento && (
                      <div className={styles.errors}>
                        {errors.establecimiento}
                      </div>
                    )}

                    <FloatingLabel
                      controlId="floatingInputUbiAdicional"
                      label="Ubicación adicional"
                      className="mb-3"
                    >
                      <Form.Control
                        type="text"
                        placeholder="Ubicación adicional"
                        name="ubi_adicional"
                        value={formData.ubi_adicional || ""}
                        onChange={handleInputChange}
                      />
                    </FloatingLabel>

                    {errors.ubi_adicional && (
                      <div className={styles.errors}>
                        {errors.ubi_adicional}
                      </div>
                    )}

                    <FloatingLabel
                      controlId="floatingInputInterno"
                      label="Interno"
                      className="mb-3"
                    >
                      <Form.Control
                        type="text"
                        placeholder="Interno"
                        name="interno"
                        value={formData.interno || ""}
                        onChange={handleInputChange}
                      />
                    </FloatingLabel>

                    {errors.interno && (
                      <div className={styles.errors}>{errors.interno}</div>
                    )}

                    <FloatingLabel
                      controlId="floatingInputCorreo"
                      label="Correo electrónico"
                      className="mb-3"
                    >
                      <Form.Control
                        type="text"
                        placeholder="Correo electrónico"
                        name="correo"
                        value={user.funcionario.correo}
                        onChange={handleInputChange}
                        disabled
                      />
                    </FloatingLabel>

                    <FloatingLabel
                      controlId="floatingInputUsuarioGde"
                      label="Usuario GDE"
                      className="mb-3"
                    >
                      <Form.Control
                        type="text"
                        placeholder="Usuario GDE"
                        name="usuario_gde"
                        value={formData.usuario_gde || ""}
                        onChange={handleInputChange}
                      />
                    </FloatingLabel>

                    {errors.usuario_gde && (
                      <div className={styles.errors}>{errors.usuario_gde}</div>
                    )}

                    <FloatingLabel
                      controlId="floatingSelectReparticionGde"
                      label="Repartición GDE"
                      className="mb-3"
                    >
                      <Form.Select
                        aria-label="Repartición GDE"
                        name="reparticion_gde.nombre"
                        value={formData.reparticion_gde?.nombre || ""}
                        onChange={handleReparticionChange}
                      >
                        <option value="" disabled>
                          Seleccionar repartición
                        </option>
                        {reparticionesGde
                          .slice()
                          .sort((a, b) => a.nombre.localeCompare(b.nombre))
                          .map((reparticion) => (
                            <option
                              key={reparticion.id}
                              value={reparticion.nombre}
                            >
                              {reparticion.nombre}
                            </option>
                          ))}
                      </Form.Select>
                    </FloatingLabel>

                    {errors.reparticion_gde && (
                      <div className={styles.errors}>
                        {errors.reparticion_gde}
                      </div>
                    )}

                    <FloatingLabel
                      controlId="floatingSelectSectorGde"
                      label="Sector GDE"
                      className="mb-3"
                    >
                      <Form.Select
                        aria-label="Sector GDE"
                        name="sector_gde.nombre"
                        value={formData.sector_gde?.nombre || ""}
                        onChange={handleSelectChange}
                      >
                        <option value="" disabled>
                          Seleccionar sector
                        </option>
                        {filteredSectores
                          .slice()
                          .sort((a, b) => a.nombre.localeCompare(b.nombre))
                          .map((sector) => (
                            <option key={sector.id} value={sector.nombre}>
                              {sector.nombre}
                            </option>
                          ))}
                      </Form.Select>
                    </FloatingLabel>

                    {errors.sector_gde && (
                      <div className={styles.errors}>{errors.sector_gde}</div>
                    )}

                    <FloatingLabel
                      controlId="floatingInputRevista"
                      label="Revista"
                      className="mb-3"
                    >
                      <Form.Control
                        type="text"
                        placeholder="Revista"
                        name="revista"
                        value={user.funcionario.revista}
                        onChange={handleInputChange}
                        disabled
                      />
                    </FloatingLabel>

                    {errors.solicitarCambios && (
                      <div className={styles.errors}>
                        {errors.solicitarCambios}
                      </div>
                    )}

                    <Button variant="primary" type="submit">
                      Solicitar cambios
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleCancel}
                      className="ms-2"
                    >
                      Cancelar
                    </Button>
                  </Form>
                ) : (
                  <>
                    <FloatingLabel
                      label="Nombre(s) y apellido(s)"
                      className="mb-3"
                    >
                      <Form.Control
                        value={user.funcionario.funcionario}
                        disabled
                      />
                    </FloatingLabel>

                    <FloatingLabel label="Orgánica" className="mb-3">
                      <Form.Control
                        value={user.funcionario.organica.nombre}
                        disabled
                      />
                    </FloatingLabel>

                    <FloatingLabel label="Establecimiento" className="mb-3">
                      <Form.Control
                        value={user.funcionario.establecimiento.nombre}
                        disabled
                      />
                    </FloatingLabel>

                    <FloatingLabel label="Ubicación adicional" className="mb-3">
                      <Form.Control
                        value={user.funcionario.ubi_adicional}
                        disabled
                      />
                    </FloatingLabel>

                    <FloatingLabel label="Interno" className="mb-3">
                      <Form.Control value={user.funcionario.interno} disabled />
                    </FloatingLabel>

                    <FloatingLabel label="Correo electrónico" className="mb-3">
                      <Form.Control value={user.funcionario.correo} disabled />
                    </FloatingLabel>

                    <FloatingLabel label="Usuario GDE" className="mb-3">
                      <Form.Control
                        value={user.funcionario.usuario_gde}
                        disabled
                      />
                    </FloatingLabel>

                    <FloatingLabel label="Repartición GDE" className="mb-3">
                      <Form.Control
                        value={user.funcionario.reparticion_gde.nombre}
                        disabled
                      />
                    </FloatingLabel>

                    <FloatingLabel label="Sector GDE" className="mb-3">
                      <Form.Control
                        value={user.funcionario.sector_gde.nombre}
                        disabled
                      />
                    </FloatingLabel>

                    <FloatingLabel label="Revista" className="mb-3">
                      <Form.Control value={user.funcionario.revista} disabled />
                    </FloatingLabel>

                    <Button
                      variant="warning"
                      onClick={async () => {
                        await loadEditModeData();
                        setEditMode(true);
                      }}
                    >
                      Editar
                    </Button>
                    {/* Toast para mostrar notificaciones */}
                    <Toast
                      onClose={() => setShowToast(false)}
                      show={showToast}
                      delay={3000}
                      autohide
                      className={`${styles.toastCenter} bg-success text-white`}
                    >
                      <Toast.Body>{toastMessage}</Toast.Body>
                    </Toast>
                  </>
                )}
              </div>
            </section>
          </Card>
        )}
      </div>
      <Footer />
    </>
  );
}

export default Usuario;
